import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminOrdersPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
    next_page_url: null,
    prev_page_url: null,
    links: []
  });
  const [filters, setFilters] = useState({
    page: 1,
    status: "",
    fname: "",
    lname: "",
    payment_code: "",
    from: "",
    to: ""
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusUpdateModal, setStatusUpdateModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchOrders(token);
  }, [navigate]);

  const fetchOrders = async (token, currentFilters = filters) => {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(currentFilters).forEach(key => {
        if (currentFilters[key]) {
          queryParams.append(key, currentFilters[key]);
        }
      });

      const response = await fetch(`${API_BASE}/orders?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle the specific API response structure for orders
        if (data.orders && Array.isArray(data.orders.data)) {
          setOrders(data.orders.data);
          // Set pagination data
          setPagination({
            current_page: data.orders.current_page,
            last_page: data.orders.last_page,
            per_page: data.orders.per_page,
            total: data.orders.total,
            from: data.orders.from,
            to: data.orders.to,
            next_page_url: data.orders.next_page_url,
            prev_page_url: data.orders.prev_page_url,
            links: data.orders.links || []
          });
        } else if (Array.isArray(data.data)) {
          setOrders(data.data);
        } else if (Array.isArray(data)) {
          setOrders(data);
        } else {
          setOrders([]);
        }
      } else {
        toast.error("Failed to fetch orders");
      }
    } catch (error) {
      toast.error("Error fetching orders");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setLoading(true);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    fetchOrders(token, filters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      page: 1,
      status: "",
      fname: "",
      lname: "",
      payment_code: "",
      from: "",
      to: ""
    };
    setFilters(clearedFilters);
    setLoading(true);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    fetchOrders(token, clearedFilters);
  };

  const handlePageChange = (page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    setLoading(true);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    fetchOrders(token, newFilters);
  };

  const handleViewOrder = (orderId) => {
    navigate(`/admin/orders/${orderId}`);
  };

  const handleUpdateStatus = (orderId) => {
    setSelectedOrder({ id: orderId });
    setStatusUpdateModal(true);
  };

  const updateOrderStatus = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    setIsUpdatingStatus(true);

    try {
      const response = await fetch(`${API_BASE}/orders/${selectedOrder.id}/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_status: newStatus
        })
      });

      if (response.ok) {
        toast.success("Order status updated successfully");
        setStatusUpdateModal(false);
        setNewStatus("");
        setSelectedOrder(null);
        fetchOrders(token);
      } else {
        toast.error("Failed to update order status");
      }
    } catch (error) {
      toast.error("Error updating order status");
      console.error("Error:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="status-badge status-pending">
          Unknown
        </span>
      );
    }
    
    const statusClasses = {
      pending: "status-pending",
      processing: "status-processing",
      shipped: "status-shipped",
      delivered: "status-delivered",
      cancelled: "status-cancelled"
    };
    
    return (
      <span className={`status-badge ${statusClasses[status.toLowerCase()] || 'status-pending'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentBadge = (paymentCode) => {
    if (!paymentCode) {
      return (
        <span className="payment-badge payment-cash">
          Unknown
        </span>
      );
    }
    
    const paymentClasses = {
      cash: "payment-cash",
      card: "payment-card",
      whish: "payment-whish"
    };
    
    return (
      <span className={`payment-badge ${paymentClasses[paymentCode.toLowerCase()] || 'payment-cash'}`}>
        {paymentCode.charAt(0).toUpperCase() + paymentCode.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-orders">
          <div className="loading">Loading orders...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-orders">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Orders Management</h1>
            <p>Manage customer orders and track their status</p>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-container">
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="fname">First Name</label>
              <input
                type="text"
                id="fname"
                name="fname"
                value={filters.fname}
                onChange={handleFilterChange}
                placeholder="Customer first name"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="lname">Last Name</label>
              <input
                type="text"
                id="lname"
                name="lname"
                value={filters.lname}
                onChange={handleFilterChange}
                placeholder="Customer last name"
              />
            </div>

            

            <div className="filter-group">
              <label htmlFor="from">From Date</label>
              <input
                type="date"
                id="from"
                name="from"
                value={filters.from}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="to">To Date</label>
              <input
                type="date"
                id="to"
                name="to"
                value={filters.to}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
            <button className="btn-primary" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Order Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.order_id}>
                    <td>
                      <strong>#{order.order_id}</strong>
                    </td>
                     <td>
                       <div className="customer-info">
                         <div className="customer-name">
                           {order.user ? `${order.user.fname} ${order.user.lname}` : 'N/A'}
                         </div>
                         <div className="customer-email">
                           {order.user ? order.user.email : 'N/A'}
                         </div>
                       </div>
                     </td>
                     <td>
                       <div className="amount-info">
                         <span className="total-amount">${order.total}</span>
                       </div>
                     </td>
                     <td>
                       {getStatusBadge(order.order_status)}
                     </td>
                     <td>
                       {getPaymentBadge(order.payment_code)}
                     </td>
                     <td>
                       {new Date(order.date_added).toLocaleDateString()}
                     </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-view"
                          onClick={() => handleViewOrder(order.order_id)}
                          title="View Details"
                        >
                          <svg viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                        <button 
                          className="btn-edit"
                          onClick={() => handleUpdateStatus(order.order_id)}
                          title="Update Status"
                        >
                          <svg viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {pagination.from} to {pagination.to} of {pagination.total} orders
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.prev_page_url}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                Previous
              </button>
              
              <div className="pagination-pages">
                {pagination.links.map((link, index) => {
                  if (link.label === '&laquo; Previous' || link.label === 'Next &raquo;') {
                    return null;
                  }
                  
                  const pageNumber = parseInt(link.label);
                  if (isNaN(pageNumber)) {
                    return null;
                  }
                  
                  return (
                    <button
                      key={index}
                      className={`pagination-page ${link.active ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.next_page_url}
              >
                Next
                <svg viewBox="0 0 24 24">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {statusUpdateModal && (
          <div className="modal-overlay" onClick={() => setStatusUpdateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Update Order Status</h2>
                <button className="modal-close" onClick={() => setStatusUpdateModal(false)}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>

              <div className="modal-form">
                <div className="form-group">
                  <label htmlFor="newStatus">New Status</label>
                  <select
                    id="newStatus"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setStatusUpdateModal(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary" onClick={updateOrderStatus} disabled={isUpdatingStatus}>
                    {isUpdatingStatus ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        Updating...
                      </>
                    ) : (
                      "Update Status"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
